/**
 * ProjectHistorySidebar — ChatGPT-style history, but for projects: the user's
 * past agent sessions AND manual generations. Each row shows the project's live
 * status via a colored dot + label, mirroring the Projects page categories
 * (Draft / Rendering / Generated / Failed). Clicking a row reopens its chat.
 *
 * Extracted from ProjectDetailPage so the new AgentProjectPage can host it; the
 * status helpers are kept identical to ProjectsPage so the states read the same
 * everywhere.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import i18n from '@/i18n';
import { MessageSquare, PanelLeftClose, Sparkles, Plus, Loader2, Trash2, Wrench } from 'lucide-react';
import { cn } from '@/v2/utils';
import { apiService } from '@/api/apiService';
import { useUserStore } from '@/store/userStore';
import type { Order } from '@/types';
import { vConfirm } from '@/v2/dialogs';
import { toast } from '@/v2/toast';

// Status colour dot — IDENTICAL to ProjectsPage.statusDotClass so the four
// categories read the same in both places.
export function statusDotClass(status?: string): string {
  switch (status) {
    case 'completed': return 'bg-emerald-400';
    case 'in_progress':
    case 'pending': return 'bg-amber-400 animate-pulse';
    case 'failed': return 'bg-red-400';
    case 'preview': return 'bg-primary';
    default: return 'bg-text-tertiary';
  }
}

// Friendly one-word status for the rows (the four project categories).
export function chatStatusLabel(status?: string): string {
  switch (status) {
    case 'chat_active': return i18n.t('projectHistory.statusDraft', { ns: 'v2' });
    case 'in_progress':
    case 'pending': return i18n.t('projectHistory.statusRendering', { ns: 'v2' });
    case 'completed': return i18n.t('projectHistory.statusGenerated', { ns: 'v2' });
    case 'failed': return i18n.t('projectHistory.statusFailed', { ns: 'v2' });
    case 'preview': return i18n.t('projectHistory.statusPreview', { ns: 'v2' });
    default: return i18n.t('projectHistory.statusProject', { ns: 'v2' });
  }
}

export function ProjectHistorySidebar({ open, onToggle, currentId, currentStatus }: { open: boolean; onToggle: () => void; currentId?: string; currentStatus?: string }) {
  const { t } = useTranslation('v2');
  const userId = useUserStore((s) => s.userId);
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      // ALL projects — agent chats AND manual-mode generations.
      const orders = await apiService.getUserOrders(userId, userId);
      setProjects([...orders].sort((a, b) => (b.orderDate || '').localeCompare(a.orderDate || '')));
    } catch { /* keep last */ } finally {
      setLoading(false);
    }
  }, [userId]);

  // Refetch on project switch AND whenever the OPEN project's status changes — so the
  // active row flips Rendering→Generated the moment the page detects it (the reported
  // bug: it used to update only when you switched projects).
  useEffect(() => { void load(); }, [load, currentId, currentStatus]);

  // Also poll while ANY project is mid-flight, so background projects (and the brief
  // gap before the active one's prop updates) refresh on their own.
  // Poll ONLY while a video is actually rendering — NOT for 'chat_active', which is a draft
  // state that persists forever (and would make this poll get-files perpetually). The open
  // project's status still updates via the currentStatus effect below, so drafts lose nothing.
  const anyActive = projects.some((p) => ['in_progress', 'pending'].includes(p.orderStatus));
  useEffect(() => {
    if (!anyActive) return;
    // Pause the orders refetch while the tab is backgrounded (resumes when visible).
    const t = setInterval(() => { if (!document.hidden) void load(); }, 8000);
    return () => clearInterval(t);
  }, [anyActive, load]);

  const remove = async (p: Order) => {
    const ok = await vConfirm(
      t('projectHistory.deleteConfirmMessage', { name: p.orderName || t('projectHistory.untitledProject') }),
      { title: t('projectHistory.deleteConfirmTitle'), confirmLabel: t('projectHistory.deleteConfirmLabel'), danger: true },
    );
    if (!ok) return;
    setDeleting(p.orderId);
    setProjects((prev) => prev.filter((x) => x.orderId !== p.orderId));
    try {
      await apiService.deleteOrder(p.orderId);
      if (p.orderId === currentId) navigate('/projects');
    } catch {
      toast.error(t('projectHistory.deleteError'));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <aside className={cn(
      // Mobile: a fixed overlay drawer that slides in/out — it NEVER pushes the chat
      // (which would crush the main content into a sliver on a phone).
      'fixed inset-y-0 left-0 z-40 w-64 transition-transform duration-300 ease-out',
      open ? 'translate-x-0' : '-translate-x-full',
      // Desktop (lg+): an in-flow column that animates its width, pushing the chat (as before).
      'lg:static lg:z-20 lg:shrink-0 lg:translate-x-0 lg:transition-[width]',
      open ? 'lg:w-64' : 'lg:w-0',
      // Shared chrome.
      'h-full border-r border-glass-border bg-surface/50 backdrop-blur-xl overflow-hidden',
    )}>
      <div className="w-64 h-full flex flex-col">
        <div className="h-14 shrink-0 flex items-center justify-between px-3 border-b border-glass-border">
          <span className="text-sm font-bold text-text-primary flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-primary" /> {t('projectHistory.projects')}
          </span>
          <button onClick={onToggle} className="w-8 h-8 rounded-lg flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-white/5 transition-colors" aria-label={t('projectHistory.hideProjects')}>
            <PanelLeftClose className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-hide">
          {loading ? (
            Array.from({ length: 7 }).map((_, i) => <div key={i} className="h-12 rounded-xl bg-surface-light/50 animate-pulse" />)
          ) : projects.length ? (
            projects.map((p) => {
              const active = p.orderId === currentId;
              return (
                <div
                  key={p.orderId}
                  className={cn(
                    'group/proj relative w-full rounded-xl border transition-all',
                    active ? 'bg-primary/10 border-primary/40 shadow-glow-sm' : 'border-transparent hover:bg-surface-light hover:border-glass-border',
                  )}
                >
                  <button
                    // The row knows which backend owns the order — pass it along so
                    // the project page's first load does not have to guess.
                    onClick={() => { if (!active) navigate(`/projects/${p.orderId}`, { state: { videoProvider: p.videoProvider ?? null } }); }}
                    className="w-full text-left px-2.5 py-2.5 pr-9 flex items-start gap-2.5"
                  >
                    <span className={cn('mt-0.5 w-6 h-6 rounded-lg flex items-center justify-center shrink-0', active ? 'bg-primary/15' : 'bg-surface-light/60')}>
                      {/* Agentic chat → 💬, one-shot auto-generate → ✨ star, manual (fal) → 🔧 */}
                      {p.operationType === 'heygen_chat'
                        ? (p.generationMode === 'auto'
                            ? <Sparkles className="w-3.5 h-3.5 text-accent" />
                            : <MessageSquare className="w-3.5 h-3.5 text-primary" />)
                        : <Wrench className="w-3.5 h-3.5 text-emerald-400" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={cn('text-sm font-medium truncate', active ? 'text-primary' : 'text-text-primary')}>{p.orderName || t('projectHistory.untitledProject')}</p>
                      <p className="text-[11px] text-text-tertiary truncate flex items-center gap-1.5">
                        <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', statusDotClass(p.orderStatus))} />
                        {chatStatusLabel(p.orderStatus)}
                      </p>
                    </div>
                  </button>
                  <button
                    onClick={() => void remove(p)}
                    disabled={deleting === p.orderId}
                    title={t('projectHistory.deleteProject')}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-lg flex items-center justify-center text-text-tertiary hover:text-error hover:bg-error/10 opacity-0 group-hover/proj:opacity-100 focus:opacity-100 transition-opacity"
                  >
                    {deleting === p.orderId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
              );
            })
          ) : (
            <p className="text-xs text-text-tertiary text-center py-8 px-3">{t('projectHistory.emptyState')}</p>
          )}
        </div>
        <button onClick={() => navigate('/')} className="m-2 shrink-0 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold hover:opacity-95 hover:shadow-glow transition-all">
          <Plus className="w-4 h-4" /> {t('projectHistory.newProject')}
        </button>
      </div>
    </aside>
  );
}
